import { Calendar, Page } from "iconoir-react";
import { useId, type ReactElement } from "react";
import {
  Badge,
  Banner,
  BodyText,
  Flex,
  LoadingAnimation,
  Stack,
} from "@knowtex-ai/components";
import useTabListKeyboard from "../../../hooks/useTabListKeyboard";
import css from "../record.module.css";
import {
  countLines,
  countWords,
  getHeaderTabClassName,
  getLeftPaneTitle,
  type LeftPaneTab,
} from "./recordLeftPaneRules";

const LEFT_PANE_TABS: LeftPaneTab[] = ["note", "chart_history"];

export interface ChartHistoryField {
  key: string;
  label: string;
  value: string;
}

export interface RecordNotePanelProps {
  activeTab: LeftPaneTab;
  onTabChange: (tab: LeftPaneTab) => void;
  patientName: string | null;
  patientStatus: string | null;
  isNoteLoading: boolean;
  noteError: string | null;
  noteContent: string;
  notePlaceholder: string;
  isCustomPromptLoading: boolean;
  hasCustomPrompt: boolean;
  isCustomPromptExpanded: boolean;
  customPromptValue: string;
  onToggleCustomPrompt: () => void;
  isChartHistoryLoading: boolean;
  chartHistoryError: string | null;
  chartHistoryFields: ChartHistoryField[];
}

export default function RecordNotePanel({
  activeTab,
  onTabChange,
  patientName,
  patientStatus,
  isNoteLoading,
  noteError,
  noteContent,
  notePlaceholder,
  isCustomPromptLoading,
  hasCustomPrompt,
  isCustomPromptExpanded,
  customPromptValue,
  onToggleCustomPrompt,
  isChartHistoryLoading,
  chartHistoryError,
  chartHistoryFields,
}: RecordNotePanelProps): ReactElement {
  const { onKeyDown, getTabIndex } = useTabListKeyboard(LEFT_PANE_TABS, activeTab, onTabChange);
  const idPrefix = useId();
  const tabId = (tab: LeftPaneTab) => `${idPrefix}-tab-${tab}`;
  const panelId = `${idPrefix}-panel`;

  return (
    <div className={css.notePanel}>
      <div className="sticky top-0 px-6 py-4 flex-shrink-0 bg-[linear-gradient(135deg,#4f46e5_0%,#6b21a8_100%)] text-white">
        <div>
          <h2 className="text-2xl font-bold text-white">{getLeftPaneTitle(activeTab)}</h2>
          {patientName ? <p className="text-sm text-white">{patientName}</p> : null}
          <div
            className="mt-3 flex gap-2"
            role="tablist"
            aria-label="Record left pane"
            onKeyDown={onKeyDown}
          >
            <button
              type="button"
              role="tab"
              id={tabId("note")}
              aria-selected={activeTab === "note"}
              aria-controls={activeTab === "note" ? panelId : undefined}
              tabIndex={getTabIndex("note")}
              onClick={() => onTabChange("note")}
              className={getHeaderTabClassName(activeTab === "note")}
            >
              Note
            </button>
            <button
              type="button"
              role="tab"
              id={tabId("chart_history")}
              aria-selected={activeTab === "chart_history"}
              aria-controls={activeTab === "chart_history" ? panelId : undefined}
              tabIndex={getTabIndex("chart_history")}
              onClick={() => onTabChange("chart_history")}
              className={getHeaderTabClassName(activeTab === "chart_history")}
            >
              Smart Chart
            </button>
          </div>
        </div>
      </div>

      <div
        className={css.notePanelBody}
        role="tabpanel"
        id={panelId}
        aria-labelledby={tabId(activeTab)}
      >
        {activeTab === "note" ? (
          <CurrentNoteContent
            patientStatus={patientStatus}
            isNoteLoading={isNoteLoading}
            noteError={noteError}
            noteContent={noteContent}
            notePlaceholder={notePlaceholder}
          />
        ) : (
          <SmartChartContent
            isCustomPromptLoading={isCustomPromptLoading}
            hasCustomPrompt={hasCustomPrompt}
            isCustomPromptExpanded={isCustomPromptExpanded}
            customPromptValue={customPromptValue}
            onToggleCustomPrompt={onToggleCustomPrompt}
            isChartHistoryLoading={isChartHistoryLoading}
            chartHistoryError={chartHistoryError}
            chartHistoryFields={chartHistoryFields}
          />
        )}
      </div>
    </div>
  );
}

function CurrentNoteContent({
  patientStatus,
  isNoteLoading,
  noteError,
  noteContent,
  notePlaceholder,
}: {
  patientStatus: string | null;
  isNoteLoading: boolean;
  noteError: string | null;
  noteContent: string;
  notePlaceholder: string;
}): ReactElement {
  const wordCount = countWords(noteContent);
  const lineCount = countLines(noteContent);

  return (
    <div className={css.noteContent}>
      {patientStatus === "Processing Audio" ? (
        <Banner variant="warning" heading="Processing Audio" className={css.bannerWarning}>
          This note will update automatically. Close and reopen to refresh.
        </Banner>
      ) : null}

      {isNoteLoading ? (
        <Stack gap={12} className="items-center py-12">
          <LoadingAnimation aria-label="Loading note" />
          <BodyText size="small" muted>
            Loading note...
          </BodyText>
        </Stack>
      ) : noteError ? (
        <Banner variant="error" className={css.bannerError}>
          {noteError}
        </Banner>
      ) : (
        <>
          <textarea
            className={css.noteDocument}
            aria-label="Current note"
            value={noteContent}
            placeholder={notePlaceholder}
            readOnly
          />
          {wordCount > 0 ? (
            <Flex alignItems="center" justifyContent="space-between" className={css.noteContentFooter}>
              <Flex alignItems="center" gap={16}>
                <Flex alignItems="center" gap={4}>
                  <Page aria-hidden="true" width={16} height={16} />
                  <BodyText size="small" muted>
                    {wordCount} words
                  </BodyText>
                </Flex>
                <Flex alignItems="center" gap={4}>
                  <Calendar aria-hidden="true" width={16} height={16} />
                  <BodyText size="small" muted>
                    {lineCount} lines
                  </BodyText>
                </Flex>
              </Flex>
              <Badge variant="warning">Read-only</Badge>
            </Flex>
          ) : null}
        </>
      )}
    </div>
  );
}

function SmartChartContent({
  isCustomPromptLoading,
  hasCustomPrompt,
  isCustomPromptExpanded,
  customPromptValue,
  onToggleCustomPrompt,
  isChartHistoryLoading,
  chartHistoryError,
  chartHistoryFields,
}: {
  isCustomPromptLoading: boolean;
  hasCustomPrompt: boolean;
  isCustomPromptExpanded: boolean;
  customPromptValue: string;
  onToggleCustomPrompt: () => void;
  isChartHistoryLoading: boolean;
  chartHistoryError: string | null;
  chartHistoryFields: ChartHistoryField[];
}): ReactElement {
  return (
    <div className="min-h-0 h-full overflow-y-auto pt-4">
      <Stack gap={16}>
        {isCustomPromptLoading && !hasCustomPrompt ? (
          <Banner variant="info">Checking for saved custom prompt...</Banner>
        ) : null}

        {hasCustomPrompt ? (
          <div className="px-4 py-3 bg-gradient-to-r from-purple-50 via-white to-indigo-50 border border-purple-300 rounded-lg shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[11px] font-extrabold uppercase tracking-[0.08em] text-purple-800">
                  Custom Prompt
                </p>
                <p className="text-sm font-medium text-gray-800">
                  A saved custom prompt is available for this patient.
                </p>
              </div>
              <button
                type="button"
                onClick={onToggleCustomPrompt}
                className="px-3 py-1.5 text-xs font-semibold rounded-full border border-purple-500 bg-purple-600 text-white hover:bg-purple-700 transition-colors shadow-sm"
              >
                {isCustomPromptExpanded ? "Hide Prompt" : "View Prompt"}
              </button>
            </div>
            {isCustomPromptExpanded ? (
              <div className="mt-3 px-4 py-3 bg-white border border-purple-200 rounded-lg">
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{customPromptValue}</p>
              </div>
            ) : null}
          </div>
        ) : null}

        {isChartHistoryLoading ? (
          <div className="flex items-center justify-center py-12">
            <div role="status" className="flex flex-col items-center gap-3">
              <svg
                aria-hidden="true"
                className="animate-spin h-8 w-8 text-accent2"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <p className="text-sm text-gray-600 font-medium">Loading chart history...</p>
            </div>
          </div>
        ) : chartHistoryError ? (
          <Banner variant="error">{chartHistoryError}</Banner>
        ) : chartHistoryFields.length === 0 ? (
          <Banner variant="info">No chart history available.</Banner>
        ) : (
          <div className="flex flex-col gap-4">
            {chartHistoryFields.map((field) => (
              <div key={field.key} className="px-4 py-3 bg-white border border-gray-200 rounded-lg">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {field.label}
                </p>
                <p className="text-sm text-gray-800 whitespace-pre-wrap mt-1">{field.value}</p>
              </div>
            ))}
          </div>
        )}
      </Stack>
    </div>
  );
}
